/**
 * GradingReport — renders a saved grading report with:
 *   - Overall PSA-style estimate + per-stage sub-grades
 *   - SVG overlay showing the detected inner-border lines
 *   - Pan + 800% zoom inspector (click on the image to zoom anchor; wheel/+/- to zoom)
 *   - Share link copy
 *
 * Pure presentational component. Fetched data shape matches the
 * /api/grading/submit and /share/:token responses.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ZOOM_LEVELS = [1, 2, 4, 6, 8];

/**
 * R3 grade→sell loop. For an identified card + predicted grade, asks the
 * buylist whether we buy it and at what cash/credit price, then offers an
 * instant "sell it to us" CTA. Renders nothing when the card isn't identified
 * or we're not currently buying it.
 */
// G4 — PSA submission concierge CTA. Shows only when the ROI calculator says
// the card is worth submitting (submit/borderline). Chains free pre-grade →
// "submit through us to PSA".
function SubmitToPsa({ report }) {
  const rec = report.roi?.recommendation;
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState('value');
  const [declared, setDeclared] = useState('');
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (rec !== 'submit' && rec !== 'borderline') return null;     // ROI gate
  const token = typeof window !== 'undefined' ? (localStorage.getItem('token') || localStorage.getItem('authToken')) : null;

  const submit = async () => {
    if (!token) { setErr('Sign in to submit cards to PSA.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/grading/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tier,
          items: [{ report_id: report.id, card_name: report.cardName || null, declared_value: Number(declared) || 0, predicted_grade: report.overall }],
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Submission failed');
      setDone(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const box = { marginTop: 12, padding: 16, borderRadius: 10, border: '1px solid #3b4cca', background: '#eef2ff' };
  if (done) {
    return (
      <div style={box}>
        <div style={{ fontWeight: 700, color: '#3b4cca' }}>Submission #{done.id} created ✓</div>
        <div style={{ marginTop: 4 }}>
          {done.item_count} card · est. <strong>${(done.total_cents / 100).toFixed(2)}</strong>
          {' '}(${(done.psa_fee_cents / 100).toFixed(2)} PSA + ${(done.service_fee_cents / 100).toFixed(2)} our service).
          We’ll email you the next steps to pay and ship.
        </div>
      </div>
    );
  }
  return (
    <div style={box}>
      <div style={{ fontWeight: 700, color: '#3b4cca' }}>Worth grading — submit it to PSA through us</div>
      <div style={{ fontSize: 14, color: '#475569', margin: '4px 0 8px' }}>
        We batch with other submissions, ship to PSA, and return the slab. No PSA account needed.
      </div>
      {!open ? (
        <button className="btn btn-primary" onClick={() => setOpen(true)}>Submit to PSA via us →</button>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Tier<br/>
            <select value={tier} onChange={e => setTier(e.target.value)}>
              <option value="value">Value (≤$499)</option>
              <option value="regular">Regular (≤$1499)</option>
              <option value="express">Express (≤$2499)</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Declared value ($)<br/>
            <input type="number" min="0" value={declared} onChange={e => setDeclared(e.target.value)} style={{ width: 110 }} placeholder="e.g. 120" />
          </label>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Submit'}</button>
        </div>
      )}
      {err && <div style={{ color: '#dc2626', marginTop: 8 }}>{err}</div>}
    </div>
  );
}

function SellOffer({ tcgCardId, overall }) {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    if (!tcgCardId || !Number.isFinite(Number(overall))) { setOffer(null); return; }
    let cancelled = false;
    fetch(`/api/buylist/offer?tcg_card_id=${encodeURIComponent(tcgCardId)}&grade=${encodeURIComponent(overall)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setOffer(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tcgCardId, overall]);

  if (!offer || !offer.buying) return null;
  return (
    <div style={{
      marginTop: 12, padding: 16, borderRadius: 10,
      border: '1px solid #16a34a', background: '#f0fdf4',
    }}>
      <div style={{ fontWeight: 700, color: '#0d5c3a', marginBottom: 4 }}>
        We'll buy this card raw — no grading wait
      </div>
      <div style={{ display: 'flex', gap: 18, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span>Condition <strong>{offer.condition}</strong></span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>${Number(offer.cash).toFixed(2)} cash</span>
        {offer.credit != null && (
          <span style={{ fontSize: 22, fontWeight: 700, color: '#0d5c3a' }}>
            ${Number(offer.credit).toFixed(2)} credit
            {offer.credit_pct > 100 && <span style={{ marginLeft: 6, background: '#16a34a', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>+{offer.credit_pct - 100}%</span>}
          </span>
        )}
      </div>
      <Link to="/sell" className="btn btn-primary" style={{ marginTop: 10, display: 'inline-block' }}>
        Sell it to us →
      </Link>
    </div>
  );
}

export default function GradingReport({ report }) {
  // Stack the two-side grid below 700px so individual card images stay
  // legible (≥320px tall on a phone) and the heatmap polygons stay tappable.
  const [stackSides, setStackSides] = useState(typeof window !== 'undefined' && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setStackSides(window.innerWidth < 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  if (!report) return null;
  const cols = report.back && !stackSides ? '1fr 1fr' : '1fr';
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 12px' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Grading report #{report.id}</h2>
        {report.cardName && <span style={{ color: '#555' }}>{report.cardName}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#666' }}>
          {report.stageVersion || 'v1-centering'} · {new Date(report.createdAt || Date.now()).toLocaleString()}
        </span>
      </header>

      <ScoreBar report={report} />

      {report.shareToken && <ShareRow token={report.shareToken} />}

      <AuthenticityBadge data={report.authenticity} />
      <RoiPanel roi={report.roi} overall={report.overall} />
      <SubmitToPsa report={report} />
      <SellOffer tcgCardId={report.tcgCardId} overall={report.overall} />

      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 16, marginTop: 16 }}>
        {report.front && <SidePanel side="Front" data={report.front} />}
        {report.back  && <SidePanel side="Back"  data={report.back}  />}
      </div>

      <FeedbackForm reportId={report.id} shareToken={report.shareToken} />
      <Disclaimer />
    </div>
  );
}

function AuthenticityBadge({ data }) {
  if (!data || data.verdict === 'unavailable') return null;
  const colors = {
    match:        { bg: '#dcfce7', fg: '#14532d', label: '✓ Matches reference' },
    likely_match: { bg: '#ecfccb', fg: '#365314', label: '≈ Likely matches reference' },
    uncertain:    { bg: '#fef3c7', fg: '#78350f', label: '? Uncertain match' },
    mismatch:     { bg: '#fee2e2', fg: '#7f1d1d', label: '⚠ Differs from reference' },
  };
  const c = colors[data.verdict] || colors.uncertain;
  return (
    <div style={{ marginTop: 12, padding: 10, background: c.bg, color: c.fg, borderRadius: 8, fontSize: 14 }}>
      <strong>{c.label}</strong> · {data.reason}
      {data.phashDistance != null && (
        <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>
          (dHash Δ {data.phashDistance}, SSIM {data.ssim?.toFixed?.(2)})
        </span>
      )}
    </div>
  );
}

function FeedbackForm({ reportId, shareToken }) {
  const [open, setOpen] = useState(false);
  const [grade, setGrade] = useState('');
  const [cert, setCert] = useState('');
  const [grader, setGrader] = useState('PSA');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  if (!reportId) return null;

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      const tokenAuth = localStorage.getItem('userToken') || '';
      const r = await fetch(`/api/grading/report/${reportId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tokenAuth ? { Authorization: `Bearer ${tokenAuth}` } : {}),
        },
        body: JSON.stringify({
          shareToken,
          actualGrade: Number(grade),
          psaCert: cert || undefined,
          grader,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div style={{ marginTop: 24, padding: 12, background: '#dcfce7', color: '#14532d', borderRadius: 8 }}>
      Thanks — this helps us calibrate future grades.
    </div>
  );

  return (
    <div style={{ marginTop: 24, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: 0, fontSize: 14 }}>
        {open ? '▾' : '▸'} Got the real grade back? Tell us how it scored
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            Grader<br />
            <select value={grader} onChange={e => setGrader(e.target.value)} style={input}>
              <option>PSA</option><option>BGS</option><option>CGC</option><option>AGS</option><option>other</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            Actual grade<br />
            <input type="number" min="1" max="10" step="0.5" value={grade}
                   onChange={e => setGrade(e.target.value)} style={{ ...input, width: 80 }} />
          </label>
          <label style={{ fontSize: 13 }}>
            Cert # (optional)<br />
            <input type="text" value={cert} onChange={e => setCert(e.target.value)}
                   style={{ ...input, width: 160 }} placeholder="e.g. 64283971" />
          </label>
          <button type="button" onClick={submit} disabled={!grade || submitting}
            style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            {submitting ? 'Sending…' : 'Submit'}
          </button>
          {err && <div role="alert" style={{ width: '100%', fontSize: 13, color: '#b91c1c' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

const input = { padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 };

function ScoreBar({ report }) {
  const o = report.overall;
  const conf = report.confidence;
  // Per-stage confidence comes from front.perStage (and back.perStage) on
  // submit responses + saved reports. We surface the FRONT stage confidences
  // here — they match the front sub-grades shown alongside.
  const ps = report.front?.perStage || {};
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, padding: '16px 0', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>AI grade</div>
        <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, color: gradeColor(o) }}>
          {o == null ? '—' : Number(o).toFixed(1).replace(/\.0$/, '')}
        </div>
      </div>
      <SubGrade label="Centering" value={report.subGrades?.centering} confidence={ps.centering?.confidence} />
      <SubGrade label="Corners"   value={report.subGrades?.corners}   confidence={ps.corners?.confidence} />
      <SubGrade label="Edges"     value={report.subGrades?.edges}     confidence={ps.edges?.confidence} />
      <SubGrade label="Surface"   value={report.subGrades?.surface}   confidence={ps.surface?.confidence} />
      <div style={{ marginLeft: 'auto' }}>
        <div style={{ fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', gap: 4 }}>
          Confidence <ConfidenceInfo />
        </div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{conf != null ? Math.round(conf * 100) + '%' : '—'}</div>
        <ConfidenceLabel value={conf} />
      </div>
    </div>
  );
}

function SubGrade({ label, value, confidence, placeholder }) {
  return (
    <div style={{ minWidth: 80, opacity: placeholder && value == null ? 0.4 : 1 }}>
      <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>
        {value == null ? (placeholder ? 'soon' : '—') : Number(value).toFixed(0)}
      </div>
      {confidence != null && <ConfidenceBar value={confidence} />}
    </div>
  );
}

/**
 * Slim per-stage confidence bar. Hue maps low → high so a struggling
 * sub-stage is visually obvious next to a confident one.
 */
function ConfidenceBar({ value }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const hue = pct >= 80 ? 142     // green
            : pct >= 60 ? 32      // amber
            :              0;     // red
  return (
    <div title={`Stage confidence ${pct}%`}
         style={{ marginTop: 4, height: 4, width: 70, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ width: pct + '%', height: '100%', background: `hsl(${hue} 70% 45%)` }} />
    </div>
  );
}

function ConfidenceLabel({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const tier =
    pct >= 80 ? { label: 'High', color: '#16a34a' } :
    pct >= 60 ? { label: 'Moderate', color: '#d97706' } :
                { label: 'Calibrating', color: '#dc2626' };
  return (
    <div style={{ fontSize: 11, color: tier.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>
      {tier.label}
    </div>
  );
}

/**
 * Inline ⓘ that explains why the number isn't 95%+. Tooltip-only on hover,
 * full text on click for touch devices.
 */
function ConfidenceInfo() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="What does this confidence number mean?"
        style={{
          display: 'inline-grid', placeItems: 'center',
          width: 16, height: 16, borderRadius: '50%',
          background: '#e2e8f0', color: '#475569',
          border: 'none', cursor: 'pointer', fontSize: 11,
          fontWeight: 700, padding: 0, lineHeight: 1,
        }}>i</button>
      {open && (
        <span
          role="tooltip"
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute', right: 0, top: 22, zIndex: 10,
            background: '#0f172a', color: '#f1f5f9',
            padding: 12, borderRadius: 6, fontSize: 12, lineHeight: 1.5,
            width: 280, boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
            cursor: 'pointer', textTransform: 'none', letterSpacing: 0,
          }}>
          <strong>Why isn't this 95%?</strong>
          <div style={{ marginTop: 6, color: '#cbd5e1' }}>
            Confidence reflects how certain each stage is in its own score.
            Centering uses precise pixel measurement; corners, edges, and
            surface currently use heuristics that cap their own confidence
            until we replace them with trained models. As real-grade feedback
            accumulates and we ship the corner ML model, confidence will rise
            on its own.
          </div>
          <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 11 }}>
            Tap to dismiss.
          </div>
        </span>
      )}
    </span>
  );
}

function gradeColor(v) {
  if (v == null) return '#777';
  if (v >= 9) return '#16a34a';
  if (v >= 7) return '#2563eb';
  if (v >= 5) return '#d97706';
  return '#dc2626';
}

function ShareRow({ token }) {
  const url = `${window.location.origin}/grade/share/${token}`;
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginTop: 8, fontSize: 13, color: '#555' }}>
      Share: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{url}</code>
      {' '}
      <button type="button"
        onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        style={{ padding: '2px 8px', fontSize: 12 }}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function SidePanel({ side, data }) {
  if (data?.error) {
    return (
      <div style={{ border: '1px solid #fed7aa', background: '#fffbeb', borderRadius: 8, padding: 12 }}>
        <strong>{side}:</strong> {data.error.message}
      </div>
    );
  }
  if (!data) return null;
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <h3 style={{ margin: '4px 0 12px', fontSize: 16 }}>{side}</h3>
      <ZoomImage data={data} />
      <div style={{ marginTop: 12, fontSize: 14 }}>
        <div><strong>Centering H:</strong> {data.ratios?.horizontal} ({data.ratios?.horizontalPct?.toFixed(1)}%)</div>
        <div><strong>Centering V:</strong> {data.ratios?.vertical} ({data.ratios?.verticalPct?.toFixed(1)}%)</div>
        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
          PSA-10 needs {side === 'Front' ? '55/45' : '75/25'}; worst-axis = {data.ratios?.worstPct?.toFixed(1)}%.
        </div>
      </div>
    </div>
  );
}

function RoiPanel({ roi, overall }) {
  if (!roi) {
    return null; // ROI is opt-in: only shown when client passed a rawValue.
  }
  if (roi.error === 'no_raw_value') return null;
  const recColor = roi.recommendation === 'submit'    ? '#16a34a'
                 : roi.recommendation === 'borderline' ? '#d97706'
                 :                                       '#dc2626';
  const recLabel = roi.recommendation === 'submit'     ? 'Worth submitting'
                 : roi.recommendation === 'borderline' ? 'Borderline'
                 : roi.recommendation === 'unknown'    ? 'Need a value'
                 :                                       'Skip submission';
  return (
    <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{
          padding: '4px 10px', borderRadius: 999, color: '#fff',
          background: recColor, fontWeight: 600, fontSize: 13,
        }}>{recLabel}</div>
        {Number.isFinite(roi.expectedNet) && (
          <div style={{ fontSize: 14 }}>
            Expected net: <strong>{roi.fees?.currency || 'USD'} {roi.expectedNet.toFixed(2)}</strong>
            {' · '}Risk-adjusted: <strong>{roi.riskAdjustedNet.toFixed(2)}</strong>
          </div>
        )}
        {roi.fees && (
          <div style={{ fontSize: 12, color: '#666', marginLeft: 'auto' }}>
            grading fee {roi.fees.gradingFee} + ship {roi.fees.shipping} {roi.fees.currency}
          </div>
        )}
      </div>
      {Array.isArray(roi.reasons) && roi.reasons.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, color: '#444' }}>
          {roi.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
    </div>
  );
}

function ZoomImage({ data }) {
  const { imageUrl, borders, workSize, heatmap } = data;
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [origin, setOrigin] = useState({ x: 50, y: 50 }); // %

  const onMove = useCallback((e) => {
    if (zoom === 1) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xpct = ((e.clientX - rect.left) / rect.width) * 100;
    const ypct = ((e.clientY - rect.top)  / rect.height) * 100;
    setOrigin({ x: Math.max(0, Math.min(100, xpct)), y: Math.max(0, Math.min(100, ypct)) });
  }, [zoom]);

  const cycleZoom = () => {
    setZoom(z => {
      const idx = ZOOM_LEVELS.indexOf(z);
      return ZOOM_LEVELS[(idx + 1) % ZOOM_LEVELS.length];
    });
  };

  if (!imageUrl) {
    return <div style={{ aspectRatio: '5/7', background: '#f3f4f6', display: 'grid', placeItems: 'center', color: '#888' }}>
      image unavailable
    </div>;
  }

  // Border lines, expressed as % of work size — overlay them on the image.
  const w = workSize?.width  || 500;
  const h = workSize?.height || 700;
  const lineLeft   = borders ? (borders.left   / w) * 100 : null;
  const lineRight  = borders ? ((w - borders.right) / w) * 100 : null;
  const lineTop    = borders ? (borders.top    / h) * 100 : null;
  const lineBottom = borders ? ((h - borders.bottom) / h) * 100 : null;

  return (
    <div>
      <div
        ref={wrapRef}
        onMouseMove={onMove}
        onClick={cycleZoom}
        title={`Click to cycle zoom (${zoom}×). Move mouse to pan.`}
        style={{
          position: 'relative',
          aspectRatio: '5/7',
          overflow: 'hidden',
          borderRadius: 6,
          background: '#000',
          cursor: zoom === 1 ? 'zoom-in' : 'zoom-out',
        }}
      >
        <img
          src={imageUrl}
          alt="card"
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${zoom})`,
            transformOrigin: `${origin.x}% ${origin.y}%`,
            transition: zoom === 1 ? 'transform 0.2s' : 'none',
            userSelect: 'none',
          }}
        />
        {/* SVG overlay tracks the same transform so border lines stay aligned. */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            transform: `scale(${zoom})`,
            transformOrigin: `${origin.x}% ${origin.y}%`,
            transition: zoom === 1 ? 'transform 0.2s' : 'none',
            pointerEvents: 'none',
          }}
        >
          {lineLeft   != null && <line x1={lineLeft}   y1="0" x2={lineLeft}   y2="100" stroke="#22d3ee" strokeWidth="0.3" />}
          {lineRight  != null && <line x1={lineRight}  y1="0" x2={lineRight}  y2="100" stroke="#22d3ee" strokeWidth="0.3" />}
          {lineTop    != null && <line x1="0" y1={lineTop}    x2="100" y2={lineTop}    stroke="#22d3ee" strokeWidth="0.3" />}
          {lineBottom != null && <line x1="0" y1={lineBottom} x2="100" y2={lineBottom} stroke="#22d3ee" strokeWidth="0.3" />}
          {/* Heatmap polygons — convert pixel coords → viewBox % using workSize. */}
          {heatmap?.polygons?.map((p, i) => (
            <rect
              key={i}
              x={(p.x / w) * 100}
              y={(p.y / h) * 100}
              width={(p.w / w) * 100}
              height={(p.h / h) * 100}
              fill={p.color}
              fillOpacity="0.30"
              stroke={p.color}
              strokeWidth="0.2"
            >
              <title>{`${p.kind} (${p.severity})${p.score != null ? ` score=${p.score}` : ''}`}</title>
            </rect>
          ))}
        </svg>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: '#555' }}>
        Zoom:
        {ZOOM_LEVELS.map(z => (
          <button key={z} type="button" onClick={() => setZoom(z)}
            style={{
              padding: '2px 8px', fontSize: 12,
              background: z === zoom ? '#2563eb' : '#fff',
              color: z === zoom ? '#fff' : '#333',
              border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer',
            }}>
            {z}×
          </button>
        ))}
      </div>
    </div>
  );
}

function Disclaimer() {
  return (
    <p style={{ marginTop: 24, fontSize: 12, color: '#777', borderTop: '1px solid #eee', paddingTop: 12 }}>
      Estimate combines centering, corners, edges, and surface heuristics.
      Heatmap colours: <span style={{ color: '#facc15' }}>yellow</span> = minor,
      {' '}<span style={{ color: '#f97316' }}>orange</span> = moderate,
      {' '}<span style={{ color: '#dc2626' }}>red</span> = severe. This is an
      unofficial pre-grade and not affiliated with PSA, BGS, CGC, or AGS.
    </p>
  );
}
